import { ApolloClient, ApolloLink, HttpOptions, InMemoryCache, createHttpLink, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { chain, set } from 'lodash';
import { v4 as uuid } from 'uuid';
import { lineItemsTableCache } from '../../components-v2/TaxDashboard/LineItemTable/cache';
import { lineItemsV2TableCache } from '../../components-v2/TaxDashboard/LineItemTableV2/cache';
import { taxDashboardCache } from '../../components-v2/TaxDashboard/cache';
import { HIJACKED_USER_ID_QUERY_ARG } from '../Auth/constants';
import { HIJACKED_USER_ID_HEADER_NAME } from '../constants';
import { errorLink } from './errorLink';
import { metadataLink } from './metadataLink';
import { retryLink } from './retryLink';

const getOperationName = (options?: RequestInit) => {
  try {
    const data = JSON.parse(options?.body as string);
    if (Array.isArray(data)) {
      return chain(data).map('operationName').join('-').value();
    }
    return data.operationName;
  } catch {
    return 'unknown';
  }
};

const DISABLE_OPERATION_BATCHING = true;

const globalFetch = window.fetch.bind(window);
const ENDPOINT_URI = process.env.GQL_URI;
const fetch: HttpOptions['fetch'] = (uri, options) => {
  // we add an `operationName` HTTP argument for developer convenience only (so it shows up in the devtools). The API does nothing with it.
  set(options!, ['headers', 'X-Correlation-ID'], uuid()); // eslint-disable-line @typescript-eslint/no-non-null-assertion
  return globalFetch(`${ENDPOINT_URI}/graph/graphql?opname=${getOperationName(options)}`, {
    ...options,
    credentials: 'include',
  });
};
const httpLink = createHttpLink({ fetch });
// https://www.apollographql.com/docs/react/api/link/apollo-link-batch-http/
const batchHttpLink = new BatchHttpLink({
  fetch,
  batchMax: 10,
  batchDebounce: true,
  batchInterval: 2000,
});
const httpPrioritySwitchingLink = split(
  (operation) => !DISABLE_OPERATION_BATCHING && operation.getContext().batched === true,
  batchHttpLink,
  httpLink,
);

const authLink = setContext((_, prevContext) => {
  const { headers } = prevContext;

  const searchParams = new URLSearchParams(window.location.search);
  const hijackedUserIdString = searchParams.get(HIJACKED_USER_ID_QUERY_ARG);

  // return the headers to the context so httpLink can read them
  return {
    ...prevContext,
    headers: {
      ...headers, // allows Authorization header to be overridden in individual calls
      ...(hijackedUserIdString
        ? {
            [HIJACKED_USER_ID_HEADER_NAME]: parseInt(hijackedUserIdString),
          }
        : {}),
    },
  };
});

export const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          ...taxDashboardCache,
          ...lineItemsTableCache,
          ...lineItemsV2TableCache,
        },
      },
      TaxLossHarvestingAsset: {
        // a row's numbers depend on the query's filters and balance date, not just its id;
        // two dates can share a balance but not a price
        keyFields: ['id', 'costBasis', 'balanceAtDate', 'priceAtDate'],
      },
      TokenOverrideConf: {
        keyFields: ['symbol'],
      },
    },
    possibleTypes: {
      // necessary as the Instruction fragment itself uses two fragments
      // see https://github.com/apollographql/apollo-client/issues/8177#issuecomment-840707458
      Instruction: ['EvmInstruction', 'IdentifierBasedInstruction'],
    },
  }),
  link: ApolloLink.from([retryLink, metadataLink, authLink, errorLink, httpPrioritySwitchingLink]),
});
